/**
 * ProductoCliente Service
 *
 * API service for managing products and their GTINs
 */
import apiClient from './apiClient';
import type {
  ProductoCliente,
  ProductoClienteCreate,
  ProductoClienteGTIN,
  ProductoClienteGTINCreate,
  BulkImportResult,
  PaginatedGTINConsulta,
} from '../types/produtoCliente';

// ========== TIPOS PARA PAGINAÇÃO ==========

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  active_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ListProductsParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
}

// ========== PRODUTOS CLIENTE ENDPOINTS ==========
// Endpoint unificado: /clientes/{clienteId}/produtos-cliente

export const produtoClienteService = {
  /**
   * List products for a client with server-side pagination
   * @param clienteId - Client ID
   * @param params - Pagination and search parameters
   * @returns Paginated response with items, total, page info
   */
  async listar(
    clienteId: number,
    params: ListProductsParams = {}
  ): Promise<PaginatedResponse<ProductoCliente>> {
    const { page = 1, page_size = 50, search } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
    });
    if (search) {
      queryParams.append('search', search);
    }
    const response = await apiClient.get(
      `/clientes/${clienteId}/produtos-cliente?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * List products with server-side pagination, sorting, and search
   */
  async listarPaginado(
    clienteId: number,
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      ordering?: string;
      showDeleted?: boolean;
    } = {}
  ): Promise<PaginatedResponse<ProductoCliente>> {
    const { page = 1, pageSize = 50, search, ordering = 'codigo', showDeleted = false } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      ordering,
    });
    if (search) queryParams.append('search', search);
    if (showDeleted) queryParams.append('show_deleted', 'true');
    const response = await apiClient.get(
      `/clientes/${clienteId}/produtos-cliente?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Fetch lightweight counts (total + active) for a client's products
   */
  async fetchCounts(clienteId: number): Promise<{ total: number; active_count: number }> {
    const response = await apiClient.get(`/clientes/${clienteId}/produtos-cliente/counts`);
    return response.data;
  },

  /**
   * List all products for a client (legacy - loads all at once)
   * @deprecated Use listar() with pagination instead for better performance
   */
  async listarTodos(clienteId: number): Promise<ProductoCliente[]> {
    // Fetch with large page size to get all
    const response = await apiClient.get(
      `/clientes/${clienteId}/produtos-cliente?page=1&page_size=100`
    );
    const data = response.data as PaginatedResponse<ProductoCliente>;

    // If total is more than page_size, fetch remaining pages
    if (data.total > data.page_size) {
      const allItems = [...data.items];
      const totalPages = data.total_pages;

      for (let page = 2; page <= totalPages; page++) {
        const pageResponse = await apiClient.get(
          `/clientes/${clienteId}/produtos-cliente?page=${page}&page_size=100`
        );
        const pageData = pageResponse.data as PaginatedResponse<ProductoCliente>;
        allItems.push(...pageData.items);
      }
      return allItems;
    }

    return data.items;
  },

  /**
   * Get product details by ID
   */
  async buscarPorId(clienteId: number, produtoId: number): Promise<ProductoCliente> {
    const response = await apiClient.get(`/clientes/${clienteId}/produtos-cliente/${produtoId}`);
    return response.data;
  },

  /**
   * Create a new product
   */
  async criar(clienteId: number, data: ProductoClienteCreate): Promise<ProductoCliente> {
    const response = await apiClient.post(`/clientes/${clienteId}/produtos-cliente`, data);
    return response.data;
  },

  /**
   * Update an existing product
   */
  async atualizar(
    clienteId: number,
    produtoId: number,
    data: Partial<ProductoClienteCreate>
  ): Promise<ProductoCliente> {
    const response = await apiClient.put(`/clientes/${clienteId}/produtos-cliente/${produtoId}`, data);
    return response.data;
  },

  /**
   * Soft delete a product
   */
  async excluir(clienteId: number, produtoId: number): Promise<void> {
    await apiClient.delete(`/clientes/${clienteId}/produtos-cliente/${produtoId}`);
  },

  /**
   * Restore a soft-deleted product (clears deleted_at and reactivates)
   */
  async restaurar(clienteId: number, produtoId: number): Promise<ProductoCliente> {
    const response = await apiClient.post(
      `/clientes/${clienteId}/produtos-cliente/${produtoId}/restore`
    );
    return response.data;
  },

  /**
   * Bulk import products from Excel file (streaming NDJSON with progress)
   */
  async importarProdutosStreaming(
    clienteId: number,
    file: File,
    onProgress: (percent: number, phase: string) => void,
  ): Promise<BulkImportResult> {
    return this._streamingUpload(
      `/clientes/${clienteId}/produtos-cliente/bulk-upload`,
      file,
      onProgress,
    );
  },

  /**
   * Bulk import GTINs from Excel file (streaming NDJSON with progress)
   */
  async importarGTINsStreaming(
    clienteId: number,
    file: File,
    onProgress: (percent: number, phase: string) => void,
  ): Promise<BulkImportResult> {
    return this._streamingUpload(
      `/clientes/${clienteId}/produtos-cliente/bulk-gtin`,
      file,
      onProgress,
    );
  },

  /**
   * Internal: streaming upload with NDJSON progress parsing
   */
  async _streamingUpload(
    url: string,
    file: File,
    onProgress: (percent: number, phase: string) => void,
  ): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    // Get auth token
    const token = await (await import('../adapters/storage')).storage.getItem('accessToken');

    // Build full URL from apiClient baseURL
    const baseURL = apiClient.defaults.baseURL || '';
    const fullUrl = `${baseURL}${url}`;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let detail = 'Erro ao importar arquivo';
      try {
        const errorJson = JSON.parse(errorText);
        detail = errorJson.detail || detail;
      } catch { /* ignore */ }
      throw new Error(detail);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: any = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;
        let msg;
        try {
          msg = JSON.parse(line);
        } catch {
          continue; // skip malformed NDJSON line
        }

        if (msg.type === 'progress') {
          const phaseLabel = msg.phase === 'parsing' ? 'Lendo Excel...'
            : msg.phase === 'parsed' ? 'Excel processado'
            : msg.phase === 'loading' ? 'Carregando dados...'
            : msg.phase === 'inserting' ? 'Salvando no banco...'
            : msg.phase || '';
          onProgress(msg.percent, phaseLabel);
        }

        if (msg.type === 'complete') {
          finalResult = msg;
          onProgress(100, 'Concluído');
        }

        if (msg.type === 'error') {
          throw new Error(msg.detail || 'Erro no processamento');
        }
      }
    }

    if (!finalResult) {
      throw new Error('Resposta incompleta do servidor');
    }

    return finalResult as BulkImportResult;
  },

  /**
   * Bulk import products from Excel file (delegates to streaming NDJSON parser)
   */
  async importarProdutos(clienteId: number, file: File): Promise<BulkImportResult> {
    return this._streamingUpload(
      `/clientes/${clienteId}/produtos-cliente/bulk-upload`,
      file,
      () => {},
    );
  },

  /**
   * Bulk import GTINs from Excel file (delegates to streaming NDJSON parser)
   */
  async importarGTINs(clienteId: number, file: File): Promise<BulkImportResult> {
    return this._streamingUpload(
      `/clientes/${clienteId}/produtos-cliente/bulk-gtin`,
      file,
      () => {},
    );
  },

  /**
   * Consulta paginada de GTINs cadastrados do cliente.
   * Cada linha representa UM GTIN. Produtos com multiplos GTINs aparecem
   * em multiplas linhas. Inclui flag is_principal e dados do produto.
   */
  async consultarGTINs(
    clienteId: number,
    params: { page?: number; pageSize?: number; search?: string } = {}
  ): Promise<PaginatedGTINConsulta> {
    const { page = 1, pageSize = 50, search } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) queryParams.append('search', search);
    const response = await apiClient.get(
      `/clientes/${clienteId}/produtos-cliente/gtins-consulta?${queryParams.toString()}`
    );
    return response.data;
  },
};

// ========== GTINS ENDPOINTS ==========
// Endpoint unificado: /clientes/{clienteId}/produtos-cliente/{produtoCodigo}/gtins

export interface ProductoClienteGTINUpdate {
  gtin?: string;
  gtin_ativo?: boolean;
}

/**
 * Story 2.22 Frente C — Endpoints CRUD de GTIN com codigo via QUERY PARAM.
 *
 * Django Ninja nao aceita "/" em path params mesmo URL-encoded (decodifica
 * %2F antes do route match e quebra a rota). Por isso usamos endpoints
 * alternativos que recebem `produto_codigo` via query param.
 *
 * URLs (lookup):
 *   GET    /clientes/{id}/produtos-cliente-gtins?produto_codigo=X
 *   POST   /clientes/{id}/produtos-cliente-gtins?produto_codigo=X
 *   PUT    /clientes/{id}/produtos-cliente-gtins/{gtin_id}
 *   DELETE /clientes/{id}/produtos-cliente-gtins/{gtin_id}
 *
 * Para PUT/DELETE o `gtin_id` ja eh chave unica suficiente — codigo nao
 * precisa entrar na URL.
 */
const buildGtinListUrl = (clienteId: number, produtoCodigo: string): string => {
  const encoded = encodeURIComponent(produtoCodigo);
  return `/clientes/${clienteId}/produtos-cliente-gtins?produto_codigo=${encoded}`;
};

const buildGtinItemUrl = (clienteId: number, gtinId: number): string => {
  return `/clientes/${clienteId}/produtos-cliente-gtins/${gtinId}`;
};

export const produtoClienteGTINService = {
  /**
   * List all GTINs for a product
   */
  async listar(clienteId: number, produtoCodigo: string): Promise<ProductoClienteGTIN[]> {
    const response = await apiClient.get(buildGtinListUrl(clienteId, produtoCodigo));
    return response.data;
  },

  /**
   * Add a new GTIN to a product
   */
  async adicionar(
    clienteId: number,
    produtoCodigo: string,
    data: ProductoClienteGTINCreate
  ): Promise<ProductoClienteGTIN> {
    const response = await apiClient.post(
      buildGtinListUrl(clienteId, produtoCodigo),
      data
    );
    return response.data;
  },

  /**
   * Update an existing GTIN
   */
  async atualizar(
    clienteId: number,
    produtoCodigo: string, // ainda recebido para compat de assinatura, mas nao usado
    gtinId: number,
    data: ProductoClienteGTINUpdate
  ): Promise<ProductoClienteGTIN> {
    const response = await apiClient.put(
      buildGtinItemUrl(clienteId, gtinId),
      data
    );
    return response.data;
  },

  /**
   * Delete a GTIN
   */
  async excluir(clienteId: number, _produtoCodigo: string, gtinId: number): Promise<void> {
    await apiClient.delete(buildGtinItemUrl(clienteId, gtinId));
  },
};

export default produtoClienteService;
